import { Link } from "react-router-dom";
import { LOGO_FORENSE, WHATSAPP_FORENSE } from "@/lib/brand";
import {
  Scale,
  ShieldCheck,
  FileText,
  Landmark,
  Gavel,
  BriefcaseBusiness,
  Home,
  Users2,
} from "lucide-react";

const peritajes = [
  "Protocolo de Estambul",
  "Daño Moral",
  "Dictámenes",
  "MetaPeritajes",
];

const pilares = [
  {
    title: "Rigor Metodológico",
    body: "Aplicación de instrumentos validados y protocolos internacionales.",
  },
  {
    title: "Independencia y Objetividad",
    body: "Apego estricto a los hallazgos científicos, libres de sesgos.",
  },
  {
    title: "Respaldo Institucional",
    body: "Procesos revisados a nivel colegial para garantizar la máxima calidad y blindaje ante los tribunales.",
  },
];

const ramas = [
  { Icon: Gavel, title: "Derecho Penal", items: ["Imputabilidad", "Estado mental", "Evaluación de víctimas"] },
  { Icon: Home, title: "Derecho Familiar", items: ["Custodia", "Alienación parental", "Idoneidad parental"] },
  { Icon: Users2, title: "Derecho Civil", items: ["Capacidad", "Daño psicológico", "Interdicciones"] },
  { Icon: BriefcaseBusiness, title: "Derecho Laboral", items: ["Mobbing", "Estrés laboral", "Riesgo psicosocial"] },
];

const Forense = () => {
  return (
    <div data-testid="forense-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grain-gold absolute inset-0" />
        <div className="relative px-6 md:px-14 pt-16 pb-14 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p
              className="eyebrow rise"
              style={{ color: "#b8975a" }}
            >
              06 · Psicología Forense y Peritaje
            </p>
            <h1
              className="mt-5 rise rise-1 gold-text"
              style={{
                fontFamily: "Cinzel Decorative, serif",
                fontSize: "clamp(2.2rem, 5vw, 4rem)",
                lineHeight: 1.05,
                letterSpacing: "0.02em",
              }}
            >
              Psicología Forense
            </h1>
            <div
              className="mt-6 rule-gold rise rise-2"
              style={{ maxWidth: 360 }}
            />
            <p
              className="mt-6 rise rise-2 max-w-xl"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                color: "#e2d6ae",
                fontSize: "1.15rem",
                lineHeight: 1.65,
              }}
            >
              Ciencia que evalúa el comportamiento humano de forma objetiva
              para aportar pruebas científicas al sistema de justicia.
            </p>
            <p
              className="mt-4 rise rise-3 max-w-xl"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                color: "rgba(226,214,174,0.75)",
                fontSize: "1.05rem",
                lineHeight: 1.65,
              }}
            >
              <strong style={{ color: "#e2c890" }}>Perito Forense:</strong>{" "}
              profesional especializado que auxilia a los jueces mediante
              dictámenes (informes técnicos) y metaperitajes (auditorías a
              otros informes) con total neutralidad y rigor legal.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 rise rise-4">
              <a href={WHATSAPP_FORENSE} target="_blank" rel="noreferrer" data-testid="forense-cta" className="btn-gold">
                Solicitar peritaje
              </a>
              <Link
                to="/"
                data-testid="forense-home-link"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full"
                style={{
                  border: "1px solid rgba(184,151,90,0.5)",
                  color: "#e2c890",
                  fontFamily: "Cormorant Garamond, serif",
                  letterSpacing: "0.02em",
                }}
              >
                Regresa a Psychelibrium, Psicología Clínica
              </Link>
            </div>
          </div>

          {/* Logo panel */}
          <div className="relative">
            <div
              className="relative rounded-sm p-10 flex flex-col items-center justify-center gold-border"
              style={{
                background:
                  "radial-gradient(circle at center, rgba(184,151,90,0.14), transparent 65%), #0b0b0c",
                minHeight: 380,
              }}
            >
              <div className="absolute top-4 left-4 right-4 rule-gold" />
              <div className="absolute bottom-4 left-4 right-4 rule-gold" />
              <div
                className="w-40 h-40 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, #f5efe6, #d8c48a 60%, #b8975a 100%)",
                  boxShadow:
                    "0 0 0 1px rgba(184,151,90,0.6), 0 20px 60px -20px rgba(184,151,90,0.4)",
                }}
              >
                <img
                  src={LOGO_FORENSE}
                  alt="Psychelibrium Forense"
                  className="w-28 h-28 object-contain"
                />
              </div>
              <div
                className="mt-6 brand-title text-2xl gold-text"
              >
                Psychelibrium
              </div>
              <div
                className="mt-2 brand-sub"
                style={{ color: "#b8975a" }}
              >
                Psicología Clínica y Forense
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Peritajes */}
      <section className="px-6 md:px-14 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <p className="eyebrow" style={{ color: "#b8975a" }}>Peritos especializados en</p>
            <h2
              className="mt-3 gold-text"
              style={{
                fontFamily: "Cinzel Decorative, serif",
                fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
              }}
            >
              Áreas de Especialidad en Peritaje Psicológico
            </h2>
            <p className="mt-4" style={{ fontFamily: "Cormorant Garamond, serif", color: "rgba(226,214,174,0.75)", fontSize: "1.05rem", lineHeight: 1.65 }}>
              Dictámenes y metaperitajes revisados a nivel colegial. Cada
              informe se elabora con base en instrumentos internacionalmente
              reconocidos.
            </p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {peritajes.map((p, i) => (
              <div
                key={p}
                data-testid={`peritaje-${i}`}
                className="rounded-sm px-6 py-5 gold-border flex items-center justify-between"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(184,151,90,0.08), rgba(184,151,90,0.02))",
                }}
              >
                <span
                  className="italic"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontSize: "1.35rem",
                    color: "#e2c890",
                  }}
                >
                  {p}
                </span>
                <FileText size={18} style={{ color: "#b8975a" }} strokeWidth={1.3} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pilares */}
      <section className="px-6 md:px-14 py-14">
        <div className="mb-8">
          <p className="eyebrow" style={{ color: "#b8975a" }}>Nuestros pilares de actuación</p>
          <h2
            className="mt-3 gold-text"
            style={{ fontFamily: "Cinzel Decorative, serif", fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
          >
            Ética, ciencia y neutralidad.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pilares.map((p, i) => (
            <article
              key={p.title}
              data-testid={`pilar-${i}`}
              className="rounded-sm p-8 gold-border"
              style={{
                background:
                  "linear-gradient(180deg, rgba(184,151,90,0.06), rgba(0,0,0,0.2))",
              }}
            >
              <ShieldCheck size={22} style={{ color: "#b8975a" }} strokeWidth={1.3} />
              <h3
                className="mt-5"
                style={{
                  fontFamily: "Cinzel, serif",
                  fontSize: "1.15rem",
                  letterSpacing: "0.08em",
                  color: "#e2c890",
                }}
              >
                {p.title}
              </h3>
              <p className="mt-3" style={{ fontFamily: "Cormorant Garamond, serif", color: "rgba(226,214,174,0.75)", lineHeight: 1.6 }}>
                {p.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Ramas del derecho */}
      <section className="px-6 md:px-14 py-14">
        <div className="mb-8">
          <p className="eyebrow" style={{ color: "#b8975a" }}>Ramas del derecho que atendemos</p>
          <h2
            className="mt-3 gold-text"
            style={{ fontFamily: "Cinzel Decorative, serif", fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
          >
            Principales &amp; Forense
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ramas.map((r, i) => (
            <article
              key={r.title}
              data-testid={`rama-${i}`}
              className="rounded-sm p-6 gold-border"
              style={{ background: "rgba(0,0,0,0.35)" }}
            >
              <r.Icon size={22} strokeWidth={1.3} style={{ color: "#b8975a" }} />
              <h3
                className="mt-4"
                style={{
                  fontFamily: "Cinzel, serif",
                  fontSize: "1rem",
                  letterSpacing: "0.14em",
                  color: "#e2c890",
                  textTransform: "uppercase",
                }}
              >
                {r.title}
              </h3>
              <ul className="mt-4 space-y-2">
                {r.items.map((it) => (
                  <li
                    key={it}
                    className="flex items-center gap-2"
                    style={{ fontFamily: "Cormorant Garamond, serif", color: "rgba(226,214,174,0.8)" }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: "#b8975a" }}
                    />
                    {it}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Asociaciones */}
      <section className="px-6 md:px-14 py-14">
        <div className="mb-8">
          <p className="eyebrow" style={{ color: "#b8975a" }}>Asociaciones y afiliaciones</p>
          <h2
            className="mt-3 gold-text"
            style={{ fontFamily: "Cinzel Decorative, serif", fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
          >
            Asociados al Colegio Nacional de Peritos y Psicólogos de México.
          </h2>
          <p
            className="mt-3 max-w-3xl"
            style={{
              fontFamily: "Cormorant Garamond, serif",
              color: "rgba(226,214,174,0.75)",
              fontSize: "1.05rem",
              lineHeight: 1.65,
            }}
          >
            CONAPPSIM y a la Fundación Yo Tú.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AsociacionCard
            testid="asoc-conappsim"
            logo="https://customer-assets.emergentagent.com/job_web-page-23/artifacts/4oxxtv42_image.png"
            name="CONAPPSIM"
            role="Colegio Nacional de Peritos y Psicólogos de México, A.C."
          />
          <AsociacionCard
            testid="asoc-yotu"
            logo="https://customer-assets.emergentagent.com/job_web-page-23/artifacts/73a5zrkc_image.png"
            name="Fundación Yo Tú"
            role="Colaboración institucional"
          />
        </div>
      </section>

      {/* CTA final */}
      <section className="px-6 md:px-14 pt-4 pb-24">
        <div
          className="rounded-sm p-8 md:p-12 gold-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          style={{
            background:
              "linear-gradient(90deg, rgba(184,151,90,0.10), rgba(0,0,0,0.4))",
          }}
        >
          <div>
            <div className="flex items-center gap-3">
              <Scale size={22} style={{ color: "#b8975a" }} />
              <p className="eyebrow" style={{ color: "#b8975a" }}>Asesoría legal-psicológica</p>
            </div>
            <h3
              className="mt-3 gold-text"
              style={{ fontFamily: "Cinzel Decorative, serif", fontSize: "clamp(1.4rem, 2.6vw, 2rem)" }}
            >
              ¿Requieres un dictamen o metaperitaje?
            </h3>
            <p className="mt-2" style={{ fontFamily: "Cormorant Garamond, serif", color: "rgba(226,214,174,0.75)", maxWidth: 640 }}>
              Coordinamos evaluación forense con estricto apego metodológico y
              acompañamiento durante el proceso judicial.
            </p>
          </div>
          <a href={WHATSAPP_FORENSE} target="_blank" rel="noreferrer" data-testid="forense-cta-final" className="btn-gold">
            <Landmark size={16} />
            Solicitar peritaje
          </a>
        </div>
      </section>
    </div>
  );
};

const AsociacionCard = ({ testid, logo, name, role }) => (
  <div
    data-testid={testid}
    className="rounded-sm p-6 md:p-7 gold-border flex items-center gap-5"
    style={{
      background:
        "linear-gradient(180deg, rgba(184,151,90,0.06), rgba(0,0,0,0.25))",
    }}
  >
    <div
      className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 30%, #f5efe6, #e8dcb8 70%, #d4b47a 100%)",
        boxShadow: "0 0 0 1px rgba(184,151,90,0.55)",
      }}
    >
      <img
        src={logo}
        alt={name}
        className="w-16 h-16 md:w-20 md:h-20 object-contain"
      />
    </div>
    <div>
      <h3
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: "1.05rem",
          letterSpacing: "0.1em",
          color: "#e2c890",
          textTransform: "uppercase",
        }}
      >
        {name}
      </h3>
      <p
        className="mt-1"
        style={{
          fontFamily: "Cormorant Garamond, serif",
          color: "rgba(226,214,174,0.75)",
          fontSize: "1rem",
          lineHeight: 1.5,
        }}
      >
        {role}
      </p>
    </div>
  </div>
);

export default Forense;
